import { useCallback, useEffect, useState } from 'react'
import { Loader2, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { getApiErrorMessage } from '@/services/api'
import { cn } from '@/lib/utils'
import cardIcon from '@/assets/invoices/card.svg'
import handWritingIcon from '@/assets/invoices/hand-writing.svg'

export interface InvoiceSendDialogDraft {
  recipient_email: string
  subject: string
  message: string
  include_bank_details: boolean
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  clientName: string
  reference: string
  totalLabel: string
  defaultTo?: string | null
  defaultSubject?: string
  defaultBody?: string
  successMessage: string
  onSend: (draft: InvoiceSendDialogDraft) => Promise<void>
  errorMessage?: string
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const fieldClass = cn(
  'rounded-xl border-0 bg-[#f4f5f8] px-4 py-2.5 text-sm text-[#171a20] placeholder:text-[#8f95a6]',
  FORM_CONTROL_FOCUS_CLASS
)

const switchClass =
  'h-6 w-10 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#d5dae3]'

function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor: string
  children: string
  required?: boolean
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='flex items-center gap-1 text-xs font-medium text-[#171a20]'
    >
      <span>{children}</span>
      {required && <span className='text-[#d63b33]'>*</span>}
    </Label>
  )
}

function SummaryRow({
  label,
  value,
  bordered = true,
  emphasized = false,
}: {
  label: string
  value: string
  bordered?: boolean
  emphasized?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 py-2 text-sm leading-5',
        bordered && 'border-b border-[#e7e9ef]'
      )}
    >
      <span className='min-w-36 text-[#6c7285]'>{label}</span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-right font-medium',
          emphasized ? 'text-[#ff5a1f]' : 'text-[#171a20]'
        )}
      >
        {value}
      </span>
    </div>
  )
}

export function InvoiceSendDialog({
  open,
  onOpenChange,
  title,
  clientName,
  reference,
  totalLabel,
  defaultTo,
  defaultSubject = '',
  defaultBody = '',
  successMessage,
  onSend,
  errorMessage,
}: Props) {
  const { t } = useTranslation('invoices')
  const resolvedErrorMessage = errorMessage ?? t('detail.sendError')
  const buildMessage = useCallback(
    (nextIncludeBankDetails: boolean, nextAllowOnlinePayment: boolean) =>
      t('detail.invoiceSendDialog.messageTemplate', {
        clientName,
        amount: totalLabel,
        reference,
        bankDetailsText: nextIncludeBankDetails
          ? t('detail.invoiceSendDialog.bankDetailsMessage')
          : '',
        onlinePaymentText: nextAllowOnlinePayment
          ? t('detail.invoiceSendDialog.onlinePaymentMessage')
          : '',
      }),
    [clientName, reference, t, totalLabel]
  )

  const initialBody = defaultBody || buildMessage(false, false)
  const [to, setTo] = useState(defaultTo ?? '')
  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState(initialBody)
  const [generatedBody, setGeneratedBody] = useState(initialBody)
  const [includeBankDetails, setIncludeBankDetails] = useState(false)
  const [allowOnlinePayment, setAllowOnlinePayment] = useState(false)
  const [toError, setToError] = useState<string | null>(null)
  const [subjectError, setSubjectError] = useState<string | null>(null)
  const [bodyError, setBodyError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  const syncGeneratedMessage = (
    nextIncludeBankDetails: boolean,
    nextAllowOnlinePayment: boolean
  ) => {
    const nextBody = buildMessage(
      nextIncludeBankDetails,
      nextAllowOnlinePayment
    )
    setGeneratedBody(nextBody)
    setBody(current => (current === generatedBody ? nextBody : current))
  }

  useEffect(() => {
    if (!open) return
    const nextBody = defaultBody || buildMessage(false, false)
    /* eslint-disable react-hooks/set-state-in-effect */
    setTo(defaultTo ?? '')
    setSubject(defaultSubject)
    setBody(nextBody)
    setGeneratedBody(nextBody)
    setIncludeBankDetails(false)
    setAllowOnlinePayment(false)
    setToError(null)
    setSubjectError(null)
    setBodyError(null)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open, defaultTo, defaultSubject, defaultBody, buildMessage])

  const submit = async () => {
    if (!to || !EMAIL_RE.test(to.trim())) {
      setToError(t('detail.invoiceSendDialog.invalidEmail'))
      return
    }
    if (!subject.trim()) {
      setSubjectError(t('detail.invoiceSendDialog.requiredSubject'))
      return
    }
    if (!body.trim()) {
      setBodyError(t('detail.invoiceSendDialog.requiredMessage'))
      return
    }

    setSending(true)
    try {
      await onSend({
        recipient_email: to.trim(),
        subject: subject.trim(),
        message: body.trim(),
        include_bank_details: includeBankDetails,
      })
      toast({ variant: 'success', title: successMessage })
      onOpenChange(false)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, resolvedErrorMessage),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-h-[calc(100vh-1.5rem)] max-w-[640px] gap-0 overflow-hidden border-[#e7e9ef] p-0 shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)] sm:rounded-2xl'
        showCloseButton={false}
      >
        <div className='max-h-[calc(100vh-1.5rem)] overflow-y-auto overflow-x-hidden px-6 py-6 sm:px-8 sm:py-8'>
          <div className='flex flex-col gap-6'>
            <DialogTitle className='pr-8 font-heading text-base font-semibold leading-[1.2] tracking-[-0.48px] text-[#171a20]'>
              {title}
            </DialogTitle>

            <div className='rounded-2xl bg-[#f4f5f8] px-4 py-2'>
              <SummaryRow
                label={t('detail.invoiceSendDialog.client')}
                value={clientName}
              />
              <SummaryRow
                label={t('detail.invoiceSendDialog.reference')}
                value={reference}
              />
              <SummaryRow
                label={t('detail.invoiceSendDialog.totalInclVat')}
                value={totalLabel}
                bordered={false}
                emphasized
              />
            </div>

            <div className='space-y-1.5'>
              <FieldLabel htmlFor='invoice-send-to' required>
                {t('detail.invoiceSendDialog.to')}
              </FieldLabel>
              <Input
                id='invoice-send-to'
                type='email'
                value={to}
                onChange={e => {
                  setTo(e.target.value)
                  setToError(null)
                }}
                className={cn(
                  fieldClass,
                  toError && 'ring-2 ring-[#d63b33]/20'
                )}
              />
              {toError && <p className='text-sm text-destructive'>{toError}</p>}
            </div>

            <div className='space-y-1.5'>
              <FieldLabel htmlFor='invoice-send-subject' required>
                {t('detail.invoiceSendDialog.subject')}
              </FieldLabel>
              <Input
                id='invoice-send-subject'
                value={subject}
                maxLength={200}
                onChange={e => {
                  setSubject(e.target.value)
                  setSubjectError(null)
                }}
                className={cn(
                  fieldClass,
                  subjectError && 'ring-2 ring-[#d63b33]/20'
                )}
              />
              {subjectError && (
                <p className='text-sm text-destructive'>{subjectError}</p>
              )}
            </div>

            <div className='space-y-1.5'>
              <FieldLabel htmlFor='invoice-send-body' required>
                {t('detail.invoiceSendDialog.message')}
              </FieldLabel>
              <Textarea
                id='invoice-send-body'
                value={body}
                maxLength={5000}
                rows={8}
                onChange={e => {
                  setBody(e.target.value)
                  setBodyError(null)
                }}
                className={cn(
                  'mb-1 min-h-[204px] rounded-xl border-0 bg-[#f4f5f8] px-4 py-3 text-sm leading-[1.4] text-[#171a20] placeholder:text-[#8f95a6]',
                  FORM_CONTROL_FOCUS_CLASS,
                  bodyError && 'ring-2 ring-[#d63b33]/20'
                )}
              />
              {bodyError && (
                <p className='text-sm text-destructive'>{bodyError}</p>
              )}
              <p className='text-sm leading-[1.4] text-[#3d434e]'>
                {t('detail.invoiceSendDialog.attachmentNote')}
              </p>
            </div>

            <div className='space-y-4'>
              <PreferenceRow
                id='invoice-include-bank-details'
                icon={handWritingIcon}
                label={t('detail.invoiceSendDialog.attachBankDetails')}
                description={t(
                  'detail.invoiceSendDialog.bankDetailsDescription'
                )}
                checked={includeBankDetails}
                onCheckedChange={checked => {
                  setIncludeBankDetails(checked)
                  syncGeneratedMessage(checked, allowOnlinePayment)
                }}
                disabled={sending}
              />
              <PreferenceRow
                id='invoice-allow-online-payment'
                icon={cardIcon}
                label={t('detail.invoiceSendDialog.onlinePayment')}
                description={t(
                  'detail.invoiceSendDialog.onlinePaymentDescription'
                )}
                checked={allowOnlinePayment}
                onCheckedChange={checked => {
                  setAllowOnlinePayment(checked)
                  syncGeneratedMessage(includeBankDetails, checked)
                }}
                disabled={sending}
              />
            </div>

            <div className='flex flex-col-reverse gap-3 sm:flex-row sm:justify-end'>
              <Button
                type='button'
                variant='outline'
                className='h-10 rounded-xl border-[#e7e9ef] px-3 text-sm font-medium text-[#171a20] hover:bg-[#fbfbfc]'
                disabled={sending}
                onClick={() => onOpenChange(false)}
              >
                {t('detail.invoiceSendDialog.cancel')}
              </Button>
              <Button
                type='button'
                className='h-10 rounded-xl bg-[#ff5a1f] px-3 text-sm font-medium text-white hover:bg-[#e6511c]'
                disabled={sending}
                onClick={submit}
              >
                {sending ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : (
                  <Send className='mr-2 size-4' />
                )}
                {t('detail.invoiceSendDialog.sendNow')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function PreferenceRow({
  id,
  icon,
  label,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string
  icon: string
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className='flex flex-col gap-0'>
      <div className='flex items-center gap-3'>
        <div className='flex min-w-0 flex-1 items-center gap-1.5'>
          <img
            alt=''
            aria-hidden='true'
            className='size-4 shrink-0'
            src={icon}
          />
          <Label
            htmlFor={id}
            className='text-sm font-medium leading-[1.4] text-[#171a20]'
          >
            {label}
          </Label>
        </div>
        <Switch
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          className={switchClass}
        />
      </div>
      <p className='text-xs leading-5 text-[#3d434e]'>{description}</p>
    </div>
  )
}
